// src/encounter/nurse-med-rec/med-rec-mapping.ts
// Medication reconciliation (Antonin's MedicineReconciliation card) ⇄ the Scrips.Patient reconciliation API (PROD-1653).
//
// The card is the design; the rules are the backend's and the spec's (Samer, 4 Nov: "the design is illustrative; development
// adopts the business logic from the back end or the spec"):
//   - The API stores one status per prescription order per encounter: Taking | NotTaking | Unverified, with a free-text note.
//     The server stamps who recorded it and when, from the signed-in user.
//   - The card's richer answer (Taking differently, the reason, the source) is kept in that note, in words.
//   - Only a medicine with a prescription order (`previousRxId`) can be recorded; any other row is shown, not saved.
//   - The nurse records; a "not taking" from a nurse is a flag for the prescriber, never a stop (Epic rooming tip sheet p4;
//     Elation "Discontinue" is prescriber-only; TJC NPSG.03.06.01 EP3). The card enforces it (`role`); this file never stops.
//   - A reading is this visit's only when it was recorded on this visit's day (the read DTO carries no encounter id). It is
//     shown as one line, never pre-filled into the card: the read has no recorder name or note, and a stamp is never invented.
// Pure; never throws.
import type { PatientMedication } from '@/types/patient-chart-api';
import type { MedicationOption } from '@/encounter/lib/to-medication-result';
import type { MedicationFormInput } from '@/encounter/lib/to-medication-request';

/** The card's answer for one medicine (structurally `MedRecEntry` in @scripsteam/signal). */
export interface CardEntry {
  status: 'as-prescribed' | 'differently' | 'not-taking' | 'stopped' | 'course-completed' | 'never-started' | 'unknown';
  how?: string;
  reason?: 'side-effects' | 'cost' | 'forgot' | 'stopped-by-other' | 'other';
  reasonText?: string;
  source: 'patient' | 'carer' | 'pharmacy' | 'other-clinician';
}

/** One medicine on the card, with the order it is recorded against (null: shown, cannot be saved). */
export interface RecMed {
  name: string;
  dose?: string;
  rxOrderId: string | null;
  /** This visit's saved status, if any (recorded on the visit's day). */
  saved?: { status: ApiStatus; at: string };
}

export type ApiStatus = 'Taking' | 'NotTaking' | 'Unverified';
export interface ApiReconciliation { encounterId: string; status: ApiStatus; note: string }

const STATUS_WORDS: Record<CardEntry['status'], string> = {
  'as-prescribed': 'Taking as prescribed',
  differently: 'Taking differently',
  'not-taking': 'Not taking',
  stopped: 'Stopped',
  'course-completed': 'Course completed',
  'never-started': 'Never started',
  unknown: 'Unknown',
};
const REASON_WORDS: Record<NonNullable<CardEntry['reason']>, string> = {
  'side-effects': 'side effects',
  cost: 'cost',
  forgot: 'forgot',
  'stopped-by-other': 'stopped by another clinician',
  other: 'other',
};
const SOURCE_WORDS: Record<CardEntry['source'], string> = {
  patient: 'Patient',
  carer: 'Carer',
  pharmacy: 'Pharmacy record',
  'other-clinician': 'Other clinician',
};

/** The API status of a card answer. Unknown is an unverified history, not a difference. */
export function apiStatus(e: CardEntry): ApiStatus {
  if (e.status === 'as-prescribed' || e.status === 'differently') return 'Taking';
  if (e.status === 'unknown') return 'Unverified';
  return 'NotTaking';
}

/** The note saved with the status: the answer in words, then where it came from. */
export function apiNote(e: CardEntry): string {
  let head = STATUS_WORDS[e.status];
  if (e.status === 'differently' && e.how?.trim()) head += `: ${e.how.trim()}`;
  if (e.status === 'not-taking' && e.reason) {
    head += e.reason === 'other' && e.reasonText?.trim() ? `: ${e.reasonText.trim()}` : `: ${REASON_WORDS[e.reason]}`;
    if (e.reason !== 'other' && e.reasonText?.trim()) head += ` (${e.reasonText.trim()})`;
  }
  return `${head}. Information from: ${SOURCE_WORDS[e.source]}.`;
}

/** An answer the card counts as recorded: Not taking needs its reason, and "Other" its words. */
export const isComplete = (e: CardEntry): boolean =>
  e.status === 'not-taking' ? !!e.reason && (e.reason !== 'other' || !!e.reasonText?.trim()) : true;

/** The request body for one answer, or null when it cannot be saved yet (incomplete, or no visit). */
export function toApi(e: CardEntry, encounterId: string | null | undefined): ApiReconciliation | null {
  if (!encounterId || !isComplete(e)) return null;
  return { encounterId, status: apiStatus(e), note: apiNote(e) };
}

/** Same calendar day (local), for "recorded on this visit's day". */
const sameDay = (iso: string, day: Date): boolean => {
  const d = new Date(iso);
  return !Number.isNaN(d.getTime()) && d.getFullYear() === day.getFullYear() && d.getMonth() === day.getMonth() && d.getDate() === day.getDate();
};

const isApiStatus = (s: unknown): s is ApiStatus => s === 'Taking' || s === 'NotTaking' || s === 'Unverified';

/** The active list as the card's medicines: deleted rows dropped, the order id kept, this visit's saved status carried. */
export function toRecMeds(list: readonly PatientMedication[] | undefined, visitDay: Date): RecMed[] {
  return (list ?? [])
    .filter((m) => m.isDeleted !== true)
    .map((m) => {
      const name = (m.code?.shortDisplay || m.code?.display || '').trim() || 'Medication';
      const dose = [m.frequency].map((x) => (x ?? '').trim()).filter(Boolean).join(' · ') || undefined;
      const at = m.reconciliationRecordedAt ?? '';
      const saved = isApiStatus(m.reconciliationStatus) && at && sameDay(at, visitDay) ? { status: m.reconciliationStatus, at } : undefined;
      return { name, dose, rxOrderId: m.previousRxId ?? null, ...(saved ? { saved } : {}) };
    });
}

const SAVED_WORDS: Record<ApiStatus, string> = { Taking: 'taking', NotTaking: 'not taking', Unverified: 'unverified' };

/** What was already saved on this visit's day, in one line (the read carries the status only, not who or the note), or null. */
export function savedTodayLine(meds: readonly RecMed[]): string | null {
  const saved = meds.filter((m) => m.saved);
  return saved.length ? `Saved earlier today: ${saved.map((m) => `${m.name}, ${SAVED_WORDS[m.saved!.status]}`).join('; ')}.` : null;
}

/** The medicines whose answer changed between two card states (added or replaced; a cleared answer is not saved). */
export function changedAnswers(
  prev: Readonly<Record<string, CardEntry>>,
  next: Readonly<Record<string, CardEntry>>,
): string[] {
  return Object.keys(next).filter((name) => {
    const a = prev[name];
    const b = next[name];
    return !a || a.status !== b.status || a.how !== b.how || a.reason !== b.reason || a.reasonText !== b.reasonText || a.source !== b.source;
  });
}

/** A medicine the patient reports, as the card hands it back (structurally Antonin's ReportedMedicine): `medication` is the coded
 *  pick from the drug search, absent when it was typed as free text (then it is not saved: an uncoded medicine would escape the
 *  interaction and allergy checks). */
export interface ReportedAdded { id: string; name: string; how: string; medication?: { value: string; label: string } }

/** The medication-list entry for a reported medicine: the coded drug the search returned, its route and unit when the catalogue
 *  has them, and the nurse's own words for how it is taken as its note. Not a prescription (CreateMedication isOrder false, set by buildCreateMedicationBody), on this visit. */
export function toReportedMedicationInput(option: MedicationOption, how?: string): MedicationFormInput {
  const route = option.route?.[0];
  const note = how?.trim();
  return {
    // How the patient says they take it, in the nurse's words: the chart shows it as the medicine's note. Never parsed into a dose.
    ...(note ? { note } : {}),
    medication: { code: option.code, display: option.name, system: option.system },
    ...(route ? { route: { code: route.code, display: route.display, ...(route.system ? { system: route.system } : {}) } } : {}),
    ...(option.unitOfMeasurement ? { unitOfMeasurement: option.unitOfMeasurement } : {}),
  };
}
